///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Story tours: authored sequences of stops (embedded scene state +
/// narrative card) with globe annotations, played back by StoryPlayer.
/// Scene state is embedded, not referenced - a stop copies its camera and
/// layers at capture time so shared stories are self-contained and
/// deleting a saved view can never break a published story. The whole
/// story is one LWW sync unit (same tradeoff as placemarks).
///

import { type CameraSavedState } from "@/types/CameraSavedState"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { validateStory } from "./validation/storiesValidation"

/**
 * Preset accent palette for story annotations. Annotations store the key;
 * the hex values drive both the globe rendering tint and UI swatches.
 * Same palette as placemarks so authored content feels consistent.
 */
export const cStoryAnnotationColors: Record<string, string> = {
	red: "#e5484d",
	orange: "#f76b15",
	amber: "#ffb224",
	green: "#46a758",
	teal: "#12a594",
	blue: "#0091ff",
	purple: "#8e4ec6",
	pink: "#d6409f",
}

export const cDefaultStoryAnnotationColor = "amber"

/** Perf/abuse budgets enforced by validation */
export const cMaxStoryStops = 50
export const cMaxStoryAnnotations = 100
export const cMaxStoryDatasets = 8
export const cMaxStoryBodyLength = 2048
export const cMaxStoryTitleLength = 200
export const cMaxAnnotationLabelLength = 120
export const cMaxAnnotationPoints = 256
export const cMaxStopSourceUrls = 8
export const cMaxStopDwellSeconds = 120

/** How a stop holds after the camera arrives */
export interface StoryDwell {
	mode: "hold" | "orbit"
	/** Guided-mode auto-advance delay; reader-paced mode ignores it */
	seconds?: number
}

/**
 * One stop of a story: an embedded scene (camera, layers, focus) plus the
 * narrative card shown while the camera dwells there.
 */
export interface StoryStop {
	/** Stable within the story (deep links: story-<id>:<n> is positional) */
	id: string
	/** Embedded pose, copied at capture time (same shape as the live camera) */
	camera: CameraSavedState
	/** Card headline */
	title: string
	/** Card narrative text, capped at cMaxStoryBodyLength */
	body: string
	/** Optional entity to re-focus on arrival (a ?target= value) */
	focusTargetId?: string
	/** Enable-only layer dependencies, like SavedView.layers */
	layers?: string[]
	/** Hold or orbit while the card reads */
	dwell: StoryDwell
	/** Story annotations visible at this stop */
	annotationIds?: string[]
	/** Cloud dataset refs (from the story pool) mounted at this stop */
	datasetIds?: string[]
	/** Citations shown on the card */
	sourceUrls?: string[]
}

/**
 * Reference to a cloud-stored user dataset the story depends on. Carries
 * enough (cdnUrl, format, filename) for playback to fetch and mount the
 * dataset — including for share recipients without an account. Stories
 * reference cloud datasets, never embed the data; a deleted dataset
 * degrades playback softly (the stop still plays without it).
 */
export interface StoryDatasetRef {
	/** Cloud dataset id (user_dataset row uuid) */
	id: string
	name: string
	filename: string
	format: "geojson" | "csv" | "image"
	cdnUrl: string
	featureCount?: number
	/** Ground overlay placement in degrees ("image" only), captured from
	 *  the author's window so per-story placement travels with the story */
	bounds?: { west: number; south: number; east: number; north: number }
	/** Ground overlay opacity 0..1 ("image" only) */
	opacity?: number
}

/** Globe overlay geometry owned by a story (not global) */
export type StoryAnnotation =
	| { id: string; kind: "arc"; points: { lat: number; lon: number }[]; label?: string; color: string; directional?: boolean }
	| { id: string; kind: "marker"; lat: number; lon: number; label?: string; color: string }
	| { id: string; kind: "region"; ring: { lat: number; lon: number }[]; label?: string; color: string }

export interface Story {
	/** uuid, generated client-side so create/update is one upsert verb */
	id: string
	title: string
	/** Ordered; empty while a draft is being authored */
	stops: StoryStop[]
	annotations: StoryAnnotation[]
	/** Cloud dataset pool; stops point in via datasetIds. Auto-pruned:
	 *  a ref no stop uses is removed by the stop-editing reducers. */
	datasets: StoryDatasetRef[]
	tags: string[]
	createdAt: string
	/** ISO date, drives sync conflict resolution (LWW, whole-story) */
	updatedAt: string
}

export interface StoriesState {
	stories: Record<string, Story>
	/** Tombstones so deletions can propagate across devices */
	deleted: Record<string, string>
	lastSyncedAt: string | null
}

const initialState: StoriesState = {
	stories: {},
	deleted: {},
	lastSyncedAt: null,
}

/** Touch a story's LWW timestamp after an in-place edit */
function touch(story: Story, updatedAt: string): void {
	story.updatedAt = updatedAt
}

/** Drop pool datasets no stop references (keeps shares/sync payloads lean) */
function pruneStoryDatasets(story: Story): void {
	if (story.datasets.length === 0) return
	const referenced = new Set<string>()
	for (const stop of story.stops) {
		for (const id of stop.datasetIds ?? []) referenced.add(id)
	}
	story.datasets = story.datasets.filter(d => referenced.has(d.id))
}

/** Merge captured refs into the pool (by id; newest ref wins) */
function mergeStoryDatasets(story: Story, datasets: StoryDatasetRef[] | undefined): void {
	if (!datasets) return
	for (const ref of datasets) {
		const index = story.datasets.findIndex(d => d.id === ref.id)
		if (index >= 0) {
			story.datasets[index] = ref
		} else if (story.datasets.length < cMaxStoryDatasets) {
			story.datasets.push(ref)
		}
	}
}

export const storiesSlice = createSlice({
	name: "stories",
	initialState,
	reducers: {
		upsertStory: (state, action: PayloadAction<Story>) => {
			const story = validateStory(action.payload)
			if (!story) return
			state.stories[story.id] = story
			// Re-creating a previously deleted id clears its tombstone
			delete state.deleted[story.id]
		},
		renameStory: {
			reducer: (state, action: PayloadAction<{ id: string; title: string; updatedAt: string }>) => {
				const { id, title, updatedAt } = action.payload
				const story = state.stories[id]
				const trimmed = typeof title === "string" ? title.trim() : ""
				if (!story || trimmed.length === 0) return
				story.title = trimmed.slice(0, cMaxStoryTitleLength)
				touch(story, updatedAt)
			},
			prepare: (payload: { id: string; title: string }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		setStoryTags: {
			reducer: (state, action: PayloadAction<{ id: string; tags: string[]; updatedAt: string }>) => {
				const { id, tags, updatedAt } = action.payload
				const story = state.stories[id]
				if (!story || !Array.isArray(tags)) return
				story.tags = tags.filter(tag => typeof tag === "string" && tag.length > 0)
				touch(story, updatedAt)
			},
			prepare: (payload: { id: string; tags: string[] }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		deleteStory: {
			reducer: (state, action: PayloadAction<{ id: string; deletedAt: string }>) => {
				const { id, deletedAt } = action.payload
				if (!state.stories[id]) return
				delete state.stories[id]
				state.deleted[id] = deletedAt
			},
			prepare: (id: string) => ({
				payload: { id, deletedAt: new Date().toISOString() },
			}),
		},

		// ========== Stop editing ==========

		/** Append a captured stop (authoring "Add stop"), optionally at an
		 *  index. Captured cloud dataset refs merge into the story pool. */
		addStop: {
			reducer: (state, action: PayloadAction<{ id: string; stop: StoryStop; datasets?: StoryDatasetRef[]; index?: number; updatedAt: string }>) => {
				const { id, stop, datasets, index, updatedAt } = action.payload
				const story = state.stories[id]
				if (!story || story.stops.length >= cMaxStoryStops) return
				if (story.stops.some(s => s.id === stop.id)) return
				mergeStoryDatasets(story, datasets)
				const at = index !== undefined
					? Math.max(0, Math.min(story.stops.length, index))
					: story.stops.length
				story.stops.splice(at, 0, stop)
				// Re-validate the whole story so a malformed stop is dropped
				const validated = validateStory(state.stories[id])
				if (!validated) return
				state.stories[id] = validated
				touch(state.stories[id], updatedAt)
			},
			prepare: (payload: { id: string; stop: StoryStop; datasets?: StoryDatasetRef[]; index?: number }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		/** Edit a stop's card / dwell / citations (undefined leaves a field alone) */
		updateStop: {
			reducer: (state, action: PayloadAction<{
				id: string
				stopId: string
				title?: string
				body?: string
				dwell?: StoryDwell
				sourceUrls?: string[]
				annotationIds?: string[]
				datasetIds?: string[]
				updatedAt: string
			}>) => {
				const { id, stopId, title, body, dwell, sourceUrls, annotationIds, datasetIds, updatedAt } = action.payload
				const story = state.stories[id]
				const stop = story?.stops.find(s => s.id === stopId)
				if (!story || !stop) return

				if (typeof title === "string") {
					stop.title = title.trim().slice(0, cMaxStoryTitleLength)
				}
				if (typeof body === "string") {
					stop.body = body.slice(0, cMaxStoryBodyLength)
				}
				if (dwell && (dwell.mode === "hold" || dwell.mode === "orbit")) {
					stop.dwell = {
						mode: dwell.mode,
						seconds: typeof dwell.seconds === "number" && isFinite(dwell.seconds)
							? Math.max(0, Math.min(cMaxStopDwellSeconds, dwell.seconds))
							: undefined,
					}
				}
				if (Array.isArray(sourceUrls)) {
					const urls = sourceUrls
						.filter(url => typeof url === "string" && /^https?:\/\//.test(url))
						.slice(0, cMaxStopSourceUrls)
					stop.sourceUrls = urls.length > 0 ? urls : undefined
				}
				if (Array.isArray(annotationIds)) {
					const known = new Set(story.annotations.map(a => a.id))
					const ids = annotationIds.filter(aid => known.has(aid))
					stop.annotationIds = ids.length > 0 ? ids : undefined
				}
				if (Array.isArray(datasetIds)) {
					const known = new Set(story.datasets.map(d => d.id))
					const ids = datasetIds.filter(did => known.has(did))
					stop.datasetIds = ids.length > 0 ? ids : undefined
					pruneStoryDatasets(story)
				}
				touch(story, updatedAt)
			},
			prepare: (payload: {
				id: string
				stopId: string
				title?: string
				body?: string
				dwell?: StoryDwell
				sourceUrls?: string[]
				annotationIds?: string[]
				datasetIds?: string[]
			}) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		/** Re-capture a stop's scene (camera/layers/focus/datasets) from the live globe */
		recaptureStop: {
			reducer: (state, action: PayloadAction<{
				id: string
				stopId: string
				camera: CameraSavedState
				layers?: string[]
				focusTargetId?: string
				datasetIds?: string[]
				datasets?: StoryDatasetRef[]
				updatedAt: string
			}>) => {
				const { id, stopId, camera, layers, focusTargetId, datasetIds, datasets, updatedAt } = action.payload
				const story = state.stories[id]
				const stop = story?.stops.find(s => s.id === stopId)
				if (!story || !stop) return

				stop.camera = { ...camera }
				stop.layers = Array.isArray(layers) && layers.length > 0 ? [...layers] : undefined
				stop.focusTargetId = typeof focusTargetId === "string" && focusTargetId.length > 0
					? focusTargetId : undefined
				mergeStoryDatasets(story, datasets)
				stop.datasetIds = Array.isArray(datasetIds) && datasetIds.length > 0 ? [...datasetIds] : undefined
				pruneStoryDatasets(story)

				// Re-validate so a corrupt capture is dropped rather than stored
				const validated = validateStory(state.stories[id])
				if (!validated) return
				state.stories[id] = validated
				touch(state.stories[id], updatedAt)
			},
			prepare: (payload: {
				id: string
				stopId: string
				camera: CameraSavedState
				layers?: string[]
				focusTargetId?: string
				datasetIds?: string[]
				datasets?: StoryDatasetRef[]
			}) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		/** Reorder: move a stop to a new index */
		moveStop: {
			reducer: (state, action: PayloadAction<{ id: string; stopId: string; index: number; updatedAt: string }>) => {
				const { id, stopId, index, updatedAt } = action.payload
				const story = state.stories[id]
				if (!story) return
				const from = story.stops.findIndex(s => s.id === stopId)
				if (from < 0) return
				const to = Math.max(0, Math.min(story.stops.length - 1, index))
				if (from === to) return
				const [stop] = story.stops.splice(from, 1)
				story.stops.splice(to, 0, stop)
				touch(story, updatedAt)
			},
			prepare: (payload: { id: string; stopId: string; index: number }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		deleteStop: {
			reducer: (state, action: PayloadAction<{ id: string; stopId: string; updatedAt: string }>) => {
				const { id, stopId, updatedAt } = action.payload
				const story = state.stories[id]
				if (!story) return
				const remaining = story.stops.filter(s => s.id !== stopId)
				if (remaining.length === story.stops.length) return
				story.stops = remaining
				pruneStoryDatasets(story)
				touch(story, updatedAt)
			},
			prepare: (payload: { id: string; stopId: string }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},

		// ========== Annotation editing ==========

		upsertAnnotation: {
			reducer: (state, action: PayloadAction<{ id: string; annotation: StoryAnnotation; updatedAt: string }>) => {
				const { id, annotation, updatedAt } = action.payload
				const story = state.stories[id]
				if (!story) return
				const index = story.annotations.findIndex(a => a.id === annotation.id)
				if (index >= 0) {
					story.annotations[index] = annotation
				} else {
					if (story.annotations.length >= cMaxStoryAnnotations) return
					story.annotations.push(annotation)
				}
				// Re-validate so malformed geometry is dropped rather than stored
				const validated = validateStory(state.stories[id])
				if (!validated) return
				state.stories[id] = validated
				touch(state.stories[id], updatedAt)
			},
			prepare: (payload: { id: string; annotation: StoryAnnotation }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		/** Delete an annotation and strip it from every stop that shows it */
		deleteAnnotation: {
			reducer: (state, action: PayloadAction<{ id: string; annotationId: string; updatedAt: string }>) => {
				const { id, annotationId, updatedAt } = action.payload
				const story = state.stories[id]
				if (!story) return
				const remaining = story.annotations.filter(a => a.id !== annotationId)
				if (remaining.length === story.annotations.length) return
				story.annotations = remaining
				for (const stop of story.stops) {
					if (!stop.annotationIds) continue
					const ids = stop.annotationIds.filter(aid => aid !== annotationId)
					stop.annotationIds = ids.length > 0 ? ids : undefined
				}
				touch(story, updatedAt)
			},
			prepare: (payload: { id: string; annotationId: string }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},

		// Applied by launch validation to repair a corrupted persisted slice
		applyValidatedStoriesState: (_state, action: PayloadAction<StoriesState>) => {
			return action.payload
		},

		// ========== Sync (server merge) ==========

		/**
		 * Apply a server delta with per-story last-write-wins. Local edits
		 * newer than the server copy survive (the orchestrator pushes them
		 * up); server tombstones remove local stories unless the local edit
		 * is newer than the deletion.
		 */
		mergeServerStories: (state, action: PayloadAction<{
			stories: Story[]
			tombstones: Array<{ id: string; deletedAt: string }>
		}>) => {
			for (const incoming of action.payload.stories) {
				const story = validateStory(incoming)
				if (!story) continue

				// A local deletion newer than the server copy wins (the
				// orchestrator pushes the delete up)
				const localDeletedAt = state.deleted[story.id]
				if (localDeletedAt && localDeletedAt >= story.updatedAt) continue

				const local = state.stories[story.id]
				if (!local || story.updatedAt > local.updatedAt) {
					state.stories[story.id] = story
					delete state.deleted[story.id]
				}
			}

			for (const tombstone of action.payload.tombstones) {
				const local = state.stories[tombstone.id]
				// A local edit newer than the deletion wins (gets pushed up)
				if (local && local.updatedAt > tombstone.deletedAt) continue
				delete state.stories[tombstone.id]
				// The server already owns this tombstone; no need to push it
				delete state.deleted[tombstone.id]
			}
		},

		/** Advance the sync cursor (only after all pushes succeeded) */
		markStoriesSynced: (state, action: PayloadAction<string>) => {
			state.lastSyncedAt = action.payload
		},

		/** Drop tombstones whose deletion reached the server */
		removeStoryTombstones: (state, action: PayloadAction<string[]>) => {
			for (const id of action.payload) {
				delete state.deleted[id]
			}
		},
	},
})

export const {
	upsertStory,
	renameStory,
	setStoryTags,
	deleteStory,
	addStop,
	updateStop,
	recaptureStop,
	moveStop,
	deleteStop,
	upsertAnnotation,
	deleteAnnotation,
	applyValidatedStoriesState,
	mergeServerStories,
	markStoriesSynced,
	removeStoryTombstones,
} = storiesSlice.actions

export default storiesSlice.reducer
